import { Clock, CheckCircle, Warning, Play } from "@phosphor-icons/react";

/**
 * Phase 265 — Job-Start status badge.
 *
 * Replaces the Acknowledge→Action badge on Jobs. Reflects:
 *   • scheduled / on_time      → "Scheduled" pill
 *   • scheduled / late_start    → AMBER "Late start"
 *   • in_progress / on_time    → "In progress"
 *   • in_progress / overdue    → AMBER "Overrunning"
 *   • in_progress / stale_24h  → CRITICAL "Stale 24h+"
 *   • completed                → emerald "Done"
 */
export default function JobStartStatusBadge({ job, className = "" }) {
  if (!job) return null;
  const status = job.status;
  const sla = job.job_start_sla || {};

  if (status === "completed") {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 ${className}`} data-testid="job-status-completed">
        <CheckCircle size={11} weight="fill" /> Done
      </span>
    );
  }
  if (status === "cancelled") {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 ${className}`} data-testid="job-status-cancelled">
        Cancelled
      </span>
    );
  }

  if (status === "in_progress") {
    if (sla.stale_overnight_alerted_at) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 animate-pulse ${className}`} title="In progress over 24 hours" data-testid="job-status-stale">
          <Warning size={11} weight="fill" /> Stale 24h+
        </span>
      );
    }
    if (sla.overdue_completion_alerted_at) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 ${className}`} title="Past scheduled end time" data-testid="job-status-overrunning">
          <Warning size={11} weight="fill" /> Overrunning
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 ${className}`} data-testid="job-status-in-progress">
        <Play size={11} weight="fill" /> In progress
      </span>
    );
  }

  // status === scheduled (default)
  if (sla.late_start_alerted_at) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 ${className}`} title="Not started 60+ min after scheduled start" data-testid="job-status-late-start">
        <Warning size={11} weight="fill" /> Late start
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 ${className}`} data-testid="job-status-scheduled">
      <Clock size={11} /> Scheduled
    </span>
  );
}

/** Left-border tint for list rows by Job-Start SLA state. */
export function jobStartRowAccent(job) {
  if (!job) return "";
  const sla = job.job_start_sla || {};
  if (sla.stale_overnight_alerted_at) return "border-l-2 border-l-red-500";
  if (sla.overdue_completion_alerted_at) return "border-l-2 border-l-amber-400";
  if (sla.late_start_alerted_at) return "border-l-2 border-l-amber-400";
  if (job.status === "completed") return "border-l-2 border-l-emerald-500";
  if (job.status === "in_progress") return "border-l-2 border-l-blue-400";
  return "";
}
